import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  ClipboardList, Loader2, CalendarIcon, ChevronLeft, 
  ChevronRight, ChevronsLeft, ChevronsRight, Filter, X, ChevronDown, Eye, Search
} from 'lucide-react';
import useThemeColors from '../hooks/useThemeColors';
import PaginationBar from '../components/PaginationBar';
import LoadErrorCard from '../components/LoadErrorCard';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';

const ACCIONES = [
  { value: 'CREATE', label: 'Crear', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'UPDATE', label: 'Actualizar', color: 'bg-blue-100 text-blue-800' },
  { value: 'DELETE', label: 'Eliminar', color: 'bg-red-100 text-red-800' },
  { value: 'LOGIN', label: 'Login', color: 'bg-purple-100 text-purple-800' },
  { value: 'LOGIN_FALLIDO', label: 'Login Fallido', color: 'bg-red-100 text-red-800' },
  { value: 'LOGOUT', label: 'Logout', color: 'bg-slate-100 text-slate-800' },
  { value: 'NOTIFICACION', label: 'Notificación', color: 'bg-amber-100 text-amber-800' },
];

const ENTIDADES = [
  { value: 'empresas', label: 'Empresas' },
  { value: 'proyectos', label: 'Proyectos' },
  { value: 'usuarios', label: 'Usuarios' },
  { value: 'reclamos', label: 'Reclamos' },
];

const AuditoriaPage = () => {
  const { api } = useAuth();
  const themeColors = useThemeColors();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  const [busquedaDebounced, setBusquedaDebounced] = useState('');
  const debounceRef = useRef(null);
  
  const [filters, setFilters] = useState({
    entidad: '',
    accion: '',
    fecha_desde: null,
    fecha_hasta: null,
  });

  // Debounce de búsqueda
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setBusquedaDebounced(busqueda);
    }, 500);
    return () => clearTimeout(debounceRef.current);
  }, [busqueda]);

  const fetchLogs = async (page = 1, signal) => {
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 50);
      
      if (filters.entidad && filters.entidad !== '__all__') params.append('entidad', filters.entidad);
      if (filters.accion && filters.accion !== '__all__') params.append('accion', filters.accion);
      if (filters.fecha_desde) params.append('fecha_desde', format(filters.fecha_desde, 'yyyy-MM-dd'));
      if (filters.fecha_hasta) params.append('fecha_hasta', format(filters.fecha_hasta, 'yyyy-MM-dd'));
      if (busquedaDebounced.trim()) params.append('busqueda', busquedaDebounced.trim());

      const response = await api.get(`/auditoria?${params}`, { signal });
      setLogs(response.data.data);
      setPagination({
        page: response.data.page,
        pages: response.data.pages,
        total: response.data.total,
      });
    } catch (err) {
      if (isCanceled(err)) return;
      console.error('Error fetching logs:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useAbortableLoad((signal) => fetchLogs(1, signal), [filters, busquedaDebounced]);

  const getAccionBadge = (accion) => {
    const accionInfo = ACCIONES.find(a => a.value === accion);
    return (
      <Badge className={accionInfo?.color || 'bg-slate-100 text-slate-800'}>
        {accionInfo?.label || accion}
      </Badge>
    );
  };

  const clearFilters = () => {
    setFilters({ entidad: '', accion: '', fecha_desde: null, fecha_hasta: null });
    setBusqueda('');
  };

  const formatJson = (data) => {
    if (!data) return null;
    try {
      return JSON.stringify(data, null, 2);
    } catch { return String(data); }
  };

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="auditoria-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">Auditoría</h1>
        <p className="text-slate-500 text-sm mt-1">Registro de todas las operaciones del sistema</p>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Filter className="w-4 h-4" />
                <span>Filtros:</span>
              </div>
              <Button variant="outline" size="sm" className="md:hidden" onClick={() => setFiltersOpen(!filtersOpen)}>
                <ChevronDown className={`w-4 h-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
              </Button>
            </div>
            <div className={`${filtersOpen ? 'block' : 'hidden'} md:block`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative w-full sm:w-[300px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar en descripción, datos anteriores, datos nuevos..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="pl-9"
                    data-testid="busqueda-input"
                  />
                </div>
                <Select value={filters.entidad} onValueChange={(value) => setFilters({ ...filters, entidad: value })}>
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="entidad-filter"><SelectValue placeholder="Entidad" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {ENTIDADES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filters.accion} onValueChange={(value) => setFilters({ ...filters, accion: value })}>
                  <SelectTrigger className="w-full sm:w-[150px]" data-testid="accion-filter"><SelectValue placeholder="Acción" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {ACCIONES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 sm:flex-none sm:w-[130px]">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {filters.fecha_desde ? format(filters.fecha_desde, 'dd/MM/yy') : 'Desde'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={filters.fecha_desde} onSelect={(date) => setFilters({ ...filters, fecha_desde: date })} locale={es} /></PopoverContent>
                  </Popover>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="flex-1 sm:flex-none sm:w-[130px]">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {filters.fecha_hasta ? format(filters.fecha_hasta, 'dd/MM/yy') : 'Hasta'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={filters.fecha_hasta} onSelect={(date) => setFilters({ ...filters, fecha_hasta: date })} locale={es} /></PopoverContent>
                  </Popover>
                </div>
                {(filters.entidad || filters.accion || filters.fecha_desde || filters.fecha_hasta || busqueda) && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}><X className="w-4 h-4 mr-1" />Limpiar</Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {loading ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          </CardContent>
        </Card>
      ) : loadError ? (
        <LoadErrorCard onRetry={() => fetchLogs(pagination.page)} testId="auditoria-load-error" />
      ) : logs.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <ClipboardList className="w-12 h-12 mb-4 text-slate-300" />
              <p>No se encontraron registros de auditoría</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Top Pagination */}
          <PaginationBar pagination={pagination} onPageChange={fetchLogs} themeColors={themeColors} />

          {/* Mobile - Cards */}
          <div className="md:hidden space-y-3">
            {logs.map((log) => (
              <Card key={log.id} className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setSelectedLog(log)} data-testid={`log-card-${log.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-semibold text-slate-900">{log.usuario_nombre || 'Sistema'}</p>
                      <p className="text-xs text-slate-500 font-mono">{format(new Date(log.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">{getAccionBadge(log.accion)}</div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Entidad:</span>
                      <span className="text-slate-800 capitalize">{log.entidad}</span>
                    </div>
                    {log.descripcion && (
                      <div className="pt-2 border-t border-slate-100">
                        <p className="text-sm text-slate-700 line-clamp-2">{log.descripcion}</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-100 flex justify-end">
                    <span className="text-xs text-slate-400 flex items-center gap-1"><Eye className="w-3 h-3" /> Ver detalle</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop - Table */}
          <Card className="border-slate-200 hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="table-header">
                      <TableHead>Fecha</TableHead>
                      <TableHead>Usuario</TableHead>
                      <TableHead>Acción</TableHead>
                      <TableHead>Entidad</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead className="hidden xl:table-cell">IP</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id} className="table-row cursor-pointer hover:bg-slate-50" onClick={() => setSelectedLog(log)} data-testid={`log-row-${log.id}`}>
                        <TableCell className="font-mono text-xs">{format(new Date(log.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{log.usuario_nombre || '-'}</TableCell>
                        <TableCell>{getAccionBadge(log.accion)}</TableCell>
                        <TableCell className="capitalize">{log.entidad}</TableCell>
                        <TableCell className="max-w-[250px] truncate text-sm text-slate-600">{log.descripcion || '-'}</TableCell>
                        <TableCell className="hidden xl:table-cell font-mono text-xs text-slate-400">{log.ip_address || '-'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setSelectedLog(log); }} data-testid={`log-detail-${log.id}`}>
                            <Eye className="w-4 h-4 text-slate-400" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Bottom Pagination */}
          <PaginationBar pagination={pagination} onPageChange={fetchLogs} themeColors={themeColors} />
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => !open && setSelectedLog(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="log-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />
              Detalle de Auditoría
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">ID Registro</p>
                  <p className="text-sm font-mono text-slate-800 break-all">{selectedLog.id}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">Fecha y Hora</p>
                  <p className="text-sm font-mono text-slate-800">{format(new Date(selectedLog.created_at), "dd/MM/yyyy HH:mm:ss", { locale: es })}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">Usuario</p>
                  <p className="text-sm text-slate-800">{selectedLog.usuario_nombre || 'Sistema'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">Acción</p>
                  {getAccionBadge(selectedLog.accion)}
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">Entidad</p>
                  <p className="text-sm text-slate-800 capitalize">{selectedLog.entidad}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-slate-500">ID Entidad</p>
                  <p className="text-sm font-mono text-slate-800 break-all">{selectedLog.entidad_id || '-'}</p>
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <p className="text-xs font-medium text-slate-500">Dirección IP</p>
                  <p className="text-sm font-mono text-slate-800">{selectedLog.ip_address || '-'}</p>
                </div>
              </div>

              {selectedLog.descripcion && (
                <div className="space-y-1 border-t border-slate-100 pt-3">
                  <p className="text-xs font-medium text-slate-500">Descripción</p>
                  <p className="text-sm text-slate-800">{selectedLog.descripcion}</p>
                </div>
              )}

              {selectedLog.datos_anteriores && (
                <div className="space-y-1 border-t border-slate-100 pt-3">
                  <p className="text-xs font-medium text-slate-500">Datos Anteriores</p>
                  <pre className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 overflow-x-auto max-h-48 text-slate-700">{formatJson(selectedLog.datos_anteriores)}</pre>
                </div>
              )}

              {selectedLog.datos_nuevos && (
                <div className="space-y-1 border-t border-slate-100 pt-3">
                  <p className="text-xs font-medium text-slate-500">Datos Nuevos</p>
                  {selectedLog.datos_nuevos?.resend_id && (
                    <div className="flex items-center gap-2 mb-1 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Resend ID:</span>
                      <code
                        className="text-[11px] font-mono bg-white px-2 py-0.5 rounded border border-blue-200 cursor-copy hover:bg-blue-100"
                        onClick={(e) => { e.stopPropagation(); try { navigator.clipboard?.writeText(selectedLog.datos_nuevos.resend_id); } catch { /* noop */ } }}
                        title="Click para copiar"
                        data-testid="audit-resend-id"
                      >
                        {selectedLog.datos_nuevos.resend_id}
                      </code>
                      <a
                        href={`https://resend.com/emails/${selectedLog.datos_nuevos.resend_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-blue-700 hover:underline"
                        title="Abrir en panel Resend"
                      >
                        Ver en Resend ↗
                      </a>
                    </div>
                  )}
                  <pre className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 overflow-x-auto max-h-48 text-slate-700">{formatJson(selectedLog.datos_nuevos)}</pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditoriaPage;
